/**
 * A price label, split into the amounts it names.
 *
 * The backend formats a price as one sentence
 * (`basePrice.label`, greencharge.access-outcome.v1):
 *
 *   "$0.175 BBD per minute + $1.00 BBD session fee (minimum $5.00 BBD)"
 *
 * On a phone that is hard to scan, so the access result step prints each
 * amount on its own row. This only CUTS the backend's sentence: every amount
 * is the backend's own text, character for character. Nothing is rounded,
 * converted or formatted here, and no price field is read.
 *
 * A label that is not in that shape (a complimentary price, a future
 * wording, a translation) returns null and the caller prints the label whole.
 */

const MINIMUM = /^(.*\S)\s+\(minimum\s+(\S.*)\)$/;
const RATE = /^(\S.*?)\s+per\s+(\S.*)$/;
const SESSION_FEE = /^(\S.*?)\s+session fee$/;

/**
 * @param {*} label
 * @returns {Array<{ id: 'rate'|'session-fee'|'minimum', term: string, value: string }>|null}
 */
export function priceLabelParts(label) {
  if (typeof label !== 'string') return null;
  let rest = label.trim();
  if (!rest) return null;

  let minimum = null;
  const withMinimum = rest.match(MINIMUM);
  if (withMinimum) {
    rest = withMinimum[1];
    minimum = withMinimum[2].trim();
  }

  const rows = [];
  for (const piece of rest.split(' + ')) {
    const part = piece.trim();
    const fee = part.match(SESSION_FEE);
    const rate = fee ? null : part.match(RATE);
    if (fee) rows.push({ id: 'session-fee', term: 'Session fee', value: fee[1] });
    else if (rate) rows.push({ id: 'rate', term: `Per ${rate[2].trim()}`, value: rate[1] });
    else return null;
  }
  if (minimum) rows.push({ id: 'minimum', term: 'Minimum charge', value: minimum });

  // Two rates, or two fees: not a sentence this knows how to cut.
  if (new Set(rows.map((row) => row.id)).size !== rows.length) return null;
  return rows;
}
